import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import AudioComparePanel from "@/components/admin/AudioComparePanel";
import ReadOnlyAdminPage from "@/components/admin/ReadOnlyAdminPage";
import BetaAdminPage from "@/components/BetaAdminPage";
import { 
  PenTool, 
  Keyboard, 
  Volume2, 
  BookOpen, 
  Plus, 
  Trash2,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Check,
  X,
  Trophy,
  Target,
  History,
  Sparkles,
  Mic,
  MicOff,
  Loader2,
  Search,
  Filter,
  Tag,
  BookText,
  Wand2,
  Music,
  Play,
  Pause,
  SkipForward,
  Gauge,
  GraduationCap,
  Edit3,
  CheckCircle2,
  MessageCircle,
  Send,
  User,
  RefreshCw,
  HelpCircle,
  Plane,
  MapPin,
  Calendar,
  Wallet,
  Info,
  ChevronDown,
  ChevronUp,
  Hash,
  XCircle,
  TrendingUp,
  Flame,
  Award,
  BarChart3,
  Settings,
  ArrowDownUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import Onboarding from "@/components/Onboarding";
import BetaGate from "@/components/BetaGate";
import DailyRecommendation from "@/components/DailyRecommendation";
import FirstSteps from "@/components/FirstSteps";

// Extracted components
import {
  ManeeChat,
  ProgressionModal,
  ClassifierExerciseModal,
  StoryGeneratorModal,
  AddWordModal,
  IntensiveTrainingModal,
  PracticeModal,
  SRSModal,
  ExportImportModal
} from "@/components";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// ========================================
// AXIOS INTERCEPTOR - Ajoute X-Beta-Code à TOUTES les requêtes
// ========================================
axios.interceptors.request.use(
  (config) => {
    const betaCode = localStorage.getItem('betaCode');
    if (betaCode) {
      config.headers['X-Beta-Code'] = betaCode;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Keep-alive ping to prevent server timeout during reading sessions
const KEEP_ALIVE_INTERVAL = 2 * 60 * 1000; // 2 minutes

const startKeepAlive = () => {
  const ping = async () => {
    try {
      await axios.get(`${API}/health`);
    } catch (err) {
      // Silent fail - just keep trying
    }
  };
  
  // Initial ping
  ping();
  
  // Set up interval
  return setInterval(ping, KEEP_ALIVE_INTERVAL);
};

// Log user activity for progress tracking
const logActivity = async (activityType, word = null, details = null) => {
  try {
    await axios.post(`${API}/progress/log`, {
      activity_type: activityType,
      word: word,
      details: details
    });
  } catch (err) {
    console.error("Error logging activity:", err);
  }
};

// Generate a unique session ID for Manee
const getManeeSessionId = () => {
  let sessionId = localStorage.getItem('ploy_session_id');
  if (!sessionId) {
    sessionId = `ploy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ploy_session_id', sessionId);
  }
  return sessionId;
};

// Character Card Component - Premium version
const CharacterCard = ({ char, info, progress, onSelect, onSpeak }) => {
  const masteryLevel = progress?.mastery_level || 0;
  const practiceCount = progress?.practice_count || 0;

  return (
    <div
      onClick={() => onSelect(char, info)}
      className="card-premium bg-white p-4 cursor-pointer group relative overflow-hidden"
      data-testid={`char-card-${char}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(char, info)}
      aria-label={`Caractère ${char}${info?.meaning ? `, ${info.meaning}` : ''}`}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 to-purple-500/0 group-hover:from-violet-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
      
      <div className="relative text-center">
        <span className="font-thai text-4xl text-slate-800 group-hover:text-violet-600 transition-colors duration-200 block">
          {char}
        </span>
        {info?.name && (
          <p className="font-thai text-xs text-slate-500 mt-1.5 truncate">{info.name}</p>
        )}
        {info?.meaning && (
          <p className="text-xs text-slate-400 mt-0.5 font-medium">{info.meaning}</p>
        )}
      </div>
      
      <div className="mt-3 flex items-center justify-between relative">
        {/* Mastery dots */}
        <div className="flex gap-1" title={`Maîtrise: ${masteryLevel}/5`}>
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`w-2 h-2 rounded-full transition-colors ${
                level <= masteryLevel ? 'bg-violet-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
        
        {/* Audio button with tooltip */}
        <button
          onClick={(e) => { e.stopPropagation(); onSpeak(char); }}
          className="p-1.5 hover:bg-violet-100 rounded-full transition-colors focus-ring"
          data-testid={`speak-char-${char}`}
          aria-label={`Écouter la prononciation de ${char}`}
          title="Écouter"
        >
          <Volume2 className="w-4 h-4 text-violet-500" />
        </button>
      </div>
      
      {practiceCount > 0 && (
        <p className="text-xs text-center text-slate-400 mt-2 font-medium">
          {practiceCount}× pratiqué
        </p>
      )}
    </div>
  );
};

// AddWordModal, IntensiveTrainingModal, StoryGeneratorModal, PracticeModal extracted to /components/modals/

// ProgressionModal extracted to /components/modals/ProgressionModal.jsx

// ClassifierExerciseModal extracted to /components/modals/ClassifierExerciseModal.jsx

// Main App
function App() {
  const [activeTab, setActiveTab] = useState("consonants");
  const [consonants, setConsonants] = useState([]);
  const [vowels, setVowels] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [classifiers, setClassifiers] = useState([]);
  const [sampleWords, setSampleWords] = useState([]);
  const [customWords, setCustomWords] = useState([]);
  const [allWords, setAllWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState({});
  const [practiceModal, setPracticeModal] = useState({ open: false, char: null, info: null, type: null });
  const [addWordModal, setAddWordModal] = useState(false);
  const [storyModal, setStoryModal] = useState(false);
  const [classifierExerciseModal, setClassifierExerciseModal] = useState(false);
  const [progressionModal, setProgressionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isManeeOpen, setIsManeeOpen] = useState(false);
  const [trainingModal, setTrainingModal] = useState(false);
  const [srsModal, setSrsModal] = useState(false);
  const [exportImportModal, setExportImportModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasBetaAccess, setHasBetaAccess] = useState(false);
  
  // First Steps state - synced with localStorage
  const [firstStepsState, setFirstStepsState] = useState(() => {
    const saved = localStorage.getItem('tp_first_steps_v1');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { step1: false, step2: false, step3: false, dismissed: false, started: false };
      }
    }
    return { step1: false, step2: false, step3: false, dismissed: false, started: false };
  });
  const [showAdminAudioPanel, setShowAdminAudioPanel] = useState(false);
  const [adminTapCount, setAdminTapCount] = useState(0);
  const adminTapTimer = useRef(null);
  
  // Mobile nav scroll indicator
  const mobileNavRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  // Check if mobile nav can scroll right
  const checkMobileNavScroll = useCallback(() => {
    const el = mobileNavRef.current;
    if (el) {
      const canScroll = el.scrollLeft < (el.scrollWidth - el.clientWidth - 5);
      setCanScrollRight(canScroll);
    }
  }, []);
  
  // Mobile nav scroll nudge (once per session)
  useEffect(() => {
    const el = mobileNavRef.current;
    if (!el) return;
    
    // Check initial scroll state
    checkMobileNavScroll();
    
    // Add scroll listener
    el.addEventListener('scroll', checkMobileNavScroll);
    
    // Nudge animation (once per session)
    if (!sessionStorage.getItem('mobileNavNudged')) {
      sessionStorage.setItem('mobileNavNudged', 'true');
      setTimeout(() => {
        if (el) {
          el.scrollTo({ left: 50, behavior: 'smooth' });
          setTimeout(() => {
            el.scrollTo({ left: 0, behavior: 'smooth' });
          }, 300);
        }
      }, 500);
    }
    
    return () => el.removeEventListener('scroll', checkMobileNavScroll);
  }, [checkMobileNavScroll]);
  
  // Admin shortcut: Ctrl+Shift+A to toggle audio compare panel (desktop)
  // OR triple-tap on app title (iPad/mobile)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdminAudioPanel(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Triple-tap handler for iPad access
  const handleTitleTap = useCallback(() => {
    setAdminTapCount(prev => {
      const newCount = prev + 1;
      
      // Clear existing timer
      if (adminTapTimer.current) {
        clearTimeout(adminTapTimer.current);
      }
      
      // Reset tap count after 1 second of no taps
      adminTapTimer.current = setTimeout(() => {
        setAdminTapCount(0);
      }, 1000);
      
      // Triple tap detected
      if (newCount >= 3) {
        setShowAdminAudioPanel(true);
        setAdminTapCount(0);
        return 0;
      }
      
      return newCount;
    });
  }, []);

  // Check beta access on mount
  useEffect(() => {
    const betaAccess = sessionStorage.getItem('beta_access');
    if (betaAccess === 'true') {
      setHasBetaAccess(true);
    }
  }, []);

  const handleBetaAccess = () => {
    setHasBetaAccess(true);
    // Carrousel Onboarding désactivé - remplacé par la carte FirstSteps
  };

  // Carrousel Onboarding désactivé - ne plus auto-afficher
  // Le useEffect qui déclenchait showOnboarding a été supprimé

  // Keep-alive to prevent server timeout during reading sessions
  useEffect(() => {
    const keepAliveInterval = startKeepAlive();
    return () => clearInterval(keepAliveInterval);
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem('thai_journal_onboarding_complete', 'true');
    setShowOnboarding(false);
  };

  // === FIRST STEPS HELPERS ===
  
  // Persist first steps state to localStorage
  const updateFirstStepsState = useCallback((newState) => {
    setFirstStepsState(newState);
    localStorage.setItem('tp_first_steps_v1', JSON.stringify(newState));
  }, []);

  // Check if first steps card should be visible (incomplete and not dismissed)
  const isFirstStepsIncomplete = !firstStepsState.step1 || !firstStepsState.step2 || !firstStepsState.step3;
  const showFirstSteps = isFirstStepsIncomplete && !firstStepsState.dismissed;
  const firstStepsAllDone = firstStepsState.step1 && firstStepsState.step2 && firstStepsState.step3 && !firstStepsState.dismissed;

  // Log first_steps_started on first render of FirstSteps card
  useEffect(() => {
    if (hasBetaAccess && showFirstSteps && !firstStepsState.started) {
      logActivity('first_steps_started');
      updateFirstStepsState({ ...firstStepsState, started: true });
    }
  }, [hasBetaAccess, showFirstSteps, firstStepsState, updateFirstStepsState]);

  // Mark step as completed and log
  const markFirstStepCompleted = useCallback((stepNum, stepName) => {
    const stepKey = `step${stepNum}`;
    if (firstStepsState[stepKey]) return; // Already done

    const newState = { ...firstStepsState, [stepKey]: true };
    updateFirstStepsState(newState);
    logActivity('onboarding_step_completed', null, { step: stepNum, name: stepName });

    // Check if all steps are now complete
    if (newState.step1 && newState.step2 && newState.step3) {
      logActivity('first_steps_completed');
    }
  }, [firstStepsState, updateFirstStepsState]);

  // First step: Start calligraphy on ก
  const handleStartCalligraphy = useCallback(() => {
    const startChar = 'ก';
    const startInfo = consonants.find(c => c.char === startChar) || { char: startChar, name: 'ก ไก่', meaning: 'poulet' };
    setPracticeModal({ open: true, char: startChar, info: startInfo, type: 'consonant' });
    logActivity('consonant_click', startChar, { type: 'consonant', source: 'first_steps' });
  }, [consonants]);

  // Encapsulated Manee opener - marks step 2
  const openManeeWithTracking = useCallback(() => {
    setIsManeeOpen(true);
    markFirstStepCompleted(2, 'Parler à Manee');
  }, [markFirstStepCompleted]);

  // Encapsulated Story opener - marks step 3
  const openStoryWithTracking = useCallback(() => {
    setStoryModal(true);
    markFirstStepCompleted(3, 'Lire une histoire');
  }, [markFirstStepCompleted]);

  // Dismiss first steps card after "Bravo"
  const handleDismissFirstSteps = useCallback(() => {
    updateFirstStepsState({ ...firstStepsState, dismissed: true });
  }, [firstStepsState, updateFirstStepsState]);

  // Handle daily recommendation actions
  const handleRecommendationAction = (actionType, params) => {
    switch (actionType) {
      case 'training':
        setTrainingModal(true);
        break;
      case 'story':
        openStoryWithTracking();
        break;
      case 'srs':
        setSrsModal(true);
        break;
      default:
        break;
    }
  };

  // Check if database needs seeding and seed if empty
  const checkAndSeedDatabase = useCallback(async () => {
    try {
      // Call health endpoint - it auto-seeds if DB is empty
      console.log("Checking health and auto-seeding if needed...");
      const healthRes = await axios.get(`${API}/health`);
      console.log("Health response:", healthRes.data);
      
      if (healthRes.data.auto_seeded) {
        toast.success(`Base de données initialisée avec ${healthRes.data.data.words_in_db} mots !`);
        return true;
      }
      
      // Double-check: if still few words, try force-seed
      if (healthRes.data.data.words_in_db < 100) {
        console.log("Still not enough words, trying force-seed...");
        setIsSeeding(true);
        toast.loading("Initialisation de la base de données...", { id: "seeding" });
        
        try {
          const seedRes = await axios.post(`${API}/words/force-seed`);
          console.log("Force seed result:", seedRes.data);
          toast.success(`Base de données initialisée !`, { id: "seeding" });
          return true;
        } catch (seedErr) {
          console.error("Force seed failed:", seedErr);
          toast.error("Erreur lors de l'initialisation", { id: "seeding" });
          return false;
        } finally {
          setIsSeeding(false);
        }
      }
      
      return false; // No seeding needed
    } catch (err) {
      console.error("Health check failed, trying direct seed:", err);
      // Fallback: try force-seed directly
      try {
        setIsSeeding(true);
        toast.loading("Initialisation de la base de données...", { id: "seeding" });
        const seedRes = await axios.post(`${API}/words/force-seed`);
        toast.success(`Base de données initialisée !`, { id: "seeding" });
        return true;
      } catch (seedErr) {
        console.error("Seed also failed:", seedErr);
        return false;
      } finally {
        setIsSeeding(false);
      }
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [consRes, vowRes, numRes, classRes, sampleRes, customRes, progressRes, statsRes, allWordsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/alphabet/consonants`),
        axios.get(`${API}/alphabet/vowels`),
        axios.get(`${API}/alphabet/numbers`),
        axios.get(`${API}/classifiers`),
        axios.get(`${API}/words/samples`),
        axios.get(`${API}/words`),
        axios.get(`${API}/progress`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/words/all`),
        axios.get(`${API}/categories`)
      ]);
      setConsonants(consRes.data);
      setVowels(vowRes.data);
      setNumbers(numRes.data);
      setClassifiers(classRes.data);
      setSampleWords(sampleRes.data);
      setCustomWords(customRes.data);
      setProgress(progressRes.data);
      setStats(statsRes.data);
      setAllWords(allWordsRes.data.words || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load with auto-seed check - ONLY after beta access is granted
  useEffect(() => {
    // Don't load data until beta access is confirmed
    if (!hasBetaAccess) return;
    
    const initApp = async () => {
      // First check and seed if needed
      const wasSeeded = await checkAndSeedDatabase();
      
      // Then fetch all data (either way)
      await fetchData();
      
      // Log session start (betaCode is guaranteed to be set at this point)
      logActivity('session_start', null, { source: 'app_open' });
      
      // If seeding was just performed, show a welcome message
      if (wasSeeded) {
        toast.success("Bienvenue ! L'application est prête à utiliser.", { duration: 3000 });
      }
    };
    
    initApp();
  }, [hasBetaAccess, checkAndSeedDatabase, fetchData]);

  const handleSpeak = async (text) => {
    try {
      const res = await axios.post(`${API}/tts?text=${encodeURIComponent(text)}`, null, {
        responseType: 'blob'
      });
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      await audio.play();
    } catch (err) {
      toast.error("Erreur lors de la lecture audio");
    }
  };

  const handleSelectCharacter = (char, info, type) => {
    setPracticeModal({ open: true, char, info, type });
    // Log consonant/vowel practice
    logActivity(type === 'consonant' ? 'consonant_click' : 'vowel_click', char, { type });
  };

  const handlePracticeComplete = async (character, charType, practiceType, completed) => {
    try {
      await axios.post(`${API}/practice`, {
        character,
        character_type: charType,
        practice_type: practiceType,
        completed
      });
      fetchData();
      // Log word practice
      logActivity('word_practice', character, { charType, practiceType, completed });
      
      // Mark first step completed if writing practice is done (calligraphy or keyboard)
      if (completed && (practiceType === 'calligraphy' || practiceType === 'keyboard')) {
        markFirstStepCompleted(1, 'Écrire ta première lettre');
      }
      
      // Ne pas fermer le modal - l'utilisateur peut continuer à pratiquer
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleSaveWord = async (formData) => {
    try {
      // Create category if it's new
      const category = formData.context;
      if (category && !categories.includes(category)) {
        try {
          await axios.post(`${API}/categories`, { name: category });
        } catch (e) {
          // Category might already exist, ignore
        }
      }
      
      // Save word with category
      await axios.post(`${API}/words`, {
        thai_word: formData.thai_word,
        french_meaning: formData.french_meaning,
        context: category,
        category: category
      });
      toast.success("Mot ajouté !");
      // Log word added activity
      logActivity('word_added', formData.thai_word, { french: formData.french_meaning, category });
      setAddWordModal(false);
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteWord = async (wordId) => {
    if (!window.confirm("Supprimer ce mot ?")) return;
    try {
      await axios.delete(`${API}/words/${wordId}`);
      toast.success("Mot supprimé !");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getProgressForChar = (char) => {
    return progress.find(p => p.character === char);
  };

  // Check for admin page route
  if (window.location.pathname === '/admin-beta-7x9k2m') {
    return <BetaAdminPage />;
  }

  // Check for readonly admin page route
  if (window.location.pathname === '/admin-readonly-7k3m9x') {
    return <ReadOnlyAdminPage />;
  }

  // Show Beta Gate first
  if (!hasBetaAccess) {
    return <BetaGate onAccess={handleBetaAccess} />;
  }

  if (loading || isSeeding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">
            {isSeeding ? "Initialisation de la base de données..." : "Chargement..."}
          </p>
          {isSeeding && (
            <p className="mt-2 text-sm text-amber-600">Ajout des mots de vocabulaire thaï...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] relative">
      <Toaster position="top-center" richColors />
      <div className="noise-overlay" />
      
      {/* Admin Audio Compare Panel (Ctrl+Shift+A on desktop, Triple-tap title on iPad) */}
      {showAdminAudioPanel && (
        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4 overflow-auto">
          <div className="relative w-full max-w-2xl">
            <button
              onClick={() => setShowAdminAudioPanel(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-sm flex items-center gap-2"
            >
              <span>✕ Fermer</span>
              <span className="text-xs opacity-70">(Triple-tap titre ou Ctrl+Shift+A)</span>
            </button>
            <AudioComparePanel />
          </div>
        </div>
      )}

      {/* Header - with safe area support for iOS notch */}
      <header className="fixed top-0 w-full z-50 glass shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
              <PenTool className="w-5 h-5 text-white" />
            </div>
            {/* Title - Triple-tap to open admin panel on iPad */}
            <div onClick={handleTitleTap} className="cursor-pointer select-none">
              <h1 className="font-heading text-xl font-bold text-slate-800" data-testid="app-title">
                ThaiPractice
              </h1>
              <p className="text-xs text-slate-500">Pratique active du thaï</p>
            </div>
          </div>

          {/* Desktop stats */}
          <div className="hidden md:flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1">
              <Target className="w-4 h-4 text-amber-500" />
              <span data-testid="stats-sessions">{stats.total_sessions || 0} sessions</span>
            </div>
            <div className="flex items-center gap-1">
              <Trophy className="w-4 h-4 text-teal-500" />
              <span data-testid="stats-mastered">{stats.characters_mastered || 0} maîtrisés</span>
            </div>
          </div>
          
          {/* Desktop action buttons */}
          <div className="hidden sm:flex items-center gap-2">
            <Button
              onClick={() => setSrsModal(true)}
              variant="outline"
              className="rounded-xl border-violet-200 text-violet-600 hover:bg-violet-50 hover:border-violet-300 transition-all"
              data-testid="srs-btn"
              title="Révision espacée"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Révision
            </Button>
            <Button
              onClick={openStoryWithTracking}
              variant="outline"
              className="rounded-xl border-fuchsia-200 text-fuchsia-600 hover:bg-fuchsia-50 hover:border-fuchsia-300 transition-all"
              data-testid="story-btn"
              title="Générateur d'histoires"
            >
              <BookText className="w-4 h-4 mr-2" />
              Histoires
            </Button>
            <Button
              onClick={() => setClassifierExerciseModal(true)}
              variant="outline"
              className="rounded-xl border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300 transition-all"
              data-testid="classifier-exercise-btn"
              title="Exercices de classificateurs"
            >
              <Hash className="w-4 h-4 mr-2" />
              Classificateurs
            </Button>
            <Button
              onClick={() => setProgressionModal(true)}
              variant="outline"
              className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 transition-all"
              data-testid="progression-btn"
              title="Voir ma progression"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Progression
            </Button>
            <Button
              onClick={() => setAddWordModal(true)}
              className="rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 shadow-lg shadow-violet-500/25 transition-all"
              data-testid="add-word-btn"
              title="Ajouter un mot personnalisé"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un mot
            </Button>
            <Button
              onClick={() => setExportImportModal(true)}
              variant="ghost"
              className="rounded-xl text-slate-500 hover:text-violet-600 hover:bg-violet-50 transition-all"
              data-testid="export-import-btn"
              title="Exporter ou importer des données"
              aria-label="Export / Import"
            >
              <ArrowDownUp className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => setTrainingModal(true)}
              variant="outline"
              className="rounded-full border-orange-300 text-orange-600 hover:bg-orange-50"
              data-testid="training-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Entraînement
            </Button>
          </div>
        </div>
        
        {/* Mobile navigation bar - horizontally scrollable with scroll indicator */}
        <div className="sm:hidden relative">
          <div 
            ref={mobileNavRef}
            className="overflow-x-auto -mx-4 px-4 pb-2" 
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="flex items-center gap-2 whitespace-nowrap" style={{ scrollSnapType: 'x mandatory' }}>
              <Button
                onClick={() => setSrsModal(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-violet-200 text-violet-600 hover:bg-violet-50 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="srs-btn-mobile"
              >
                <GraduationCap className="w-4 h-4 mr-1.5" />
                Révision
              </Button>
              <Button
                onClick={openStoryWithTracking}
                variant="outline"
                size="sm"
                className="rounded-full border-fuchsia-200 text-fuchsia-600 hover:bg-fuchsia-50 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="story-btn-mobile"
              >
                <BookText className="w-4 h-4 mr-1.5" />
                Histoires
              </Button>
              <Button
                onClick={() => setClassifierExerciseModal(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-emerald-200 text-emerald-600 hover:bg-emerald-50 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="classifier-exercise-btn-mobile"
              >
                <Hash className="w-4 h-4 mr-1.5" />
                Classificateurs
              </Button>
              <Button
                onClick={() => setProgressionModal(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="progression-btn-mobile"
              >
                <BarChart3 className="w-4 h-4 mr-1.5" />
                Stats
              </Button>
              <Button
                onClick={() => setAddWordModal(true)}
                size="sm"
                className="rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="add-word-btn-mobile"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Mot
              </Button>
              <Button
                onClick={() => setTrainingModal(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-orange-300 text-orange-600 hover:bg-orange-50 flex-shrink-0 whitespace-nowrap"
                style={{ scrollSnapAlign: 'start' }}
                data-testid="training-btn-mobile"
              >
                <Target className="w-4 h-4 mr-1.5" />
                Entraînement
              </Button>
            </div>
          </div>
          {/* Scroll indicator: gradient + chevron */}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-2 w-12 pointer-events-none flex items-center justify-end pr-1">
              <div className="absolute inset-0 bg-gradient-to-l from-white via-white/80 to-transparent" />
              <ChevronRight className="w-5 h-5 text-slate-400 relative z-10 animate-pulse" />
            </div>
          )}
        </div>
      </header>

      {/* Main Content - extra padding for iOS safe areas and Manee button */}
      <main className="relative z-10 px-4 sm:px-6" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 120px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 160px)' }}>
        <div className="max-w-6xl mx-auto">
          
          {/* First Steps card OR Daily Recommendation - never both */}
          {showFirstSteps || firstStepsAllDone ? (
            <FirstSteps
              stepsCompleted={{
                step1: firstStepsState.step1,
                step2: firstStepsState.step2,
                step3: firstStepsState.step3
              }}
              onStartCalligraphy={handleStartCalligraphy}
              onOpenManee={openManeeWithTracking}
              onOpenStory={openStoryWithTracking}
              allDone={firstStepsAllDone}
              onDismiss={handleDismissFirstSteps}
            />
          ) : (
            <DailyRecommendation stats={stats} onAction={handleRecommendationAction} />
          )}
          
          {/* Stats Cards - Premium Style with Tooltips */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card group" title="Sessions d'entraînement en calligraphie">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <PenTool className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.calligraphy_sessions || 0}</p>
                  <p className="text-xs text-slate-500 font-medium">Calligraphie</p>
                </div>
              </div>
            </div>
            <div className="stat-card group" title="Sessions d'entraînement au clavier">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Keyboard className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.keyboard_sessions || 0}</p>
                  <p className="text-xs text-slate-500 font-medium">Clavier</p>
                </div>
              </div>
            </div>
            <div className="stat-card group" title="Mots personnalisés ajoutés à ton vocabulaire">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-violet-100 to-purple-100 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <BookOpen className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total_custom_words || 0}</p>
                  <p className="text-xs text-slate-500 font-medium">Mes mots</p>
                </div>
              </div>
            </div>
            <div className="stat-card group" title="Caractères maîtrisés parmi ceux pratiqués">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {stats.characters_mastered || 0}
                  </p>
                  <p className="text-xs text-slate-500 font-medium">Maîtrisés</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs - Premium Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 bg-white/90 backdrop-blur-sm p-1.5 rounded-2xl shadow-sm border border-violet-100/50 flex-wrap gap-1">
              <TabsTrigger 
                value="consonants" 
                data-testid="tab-consonants"
                className="rounded-xl data-[state=active]:bg-violet-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Consonnes ({consonants.length})
              </TabsTrigger>
              <TabsTrigger 
                value="words" 
                data-testid="tab-words"
                className="rounded-xl data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Mots ({sampleWords.length + customWords.length})
              </TabsTrigger>
              <TabsTrigger 
                value="vowels" 
                data-testid="tab-vowels"
                className="rounded-xl data-[state=active]:bg-fuchsia-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Voyelles ({vowels.length})
              </TabsTrigger>
              <TabsTrigger 
                value="numbers" 
                data-testid="tab-numbers"
                className="rounded-xl data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Chiffres ({numbers.length})
              </TabsTrigger>
              <TabsTrigger 
                value="classifiers" 
                data-testid="tab-classifiers"
                className="rounded-xl data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              >
                Classificateurs ({classifiers.length})
              </TabsTrigger>
            </TabsList>

            {/* Consonants */}
            <TabsContent value="consonants" className="animate-fade-in">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-11 gap-3" data-testid="consonants-grid">
                {consonants.map((c) => (
                  <CharacterCard
                    key={c.char}
                    char={c.char}
                    info={c}
                    progress={getProgressForChar(c.char)}
                    onSelect={(char, info) => handleSelectCharacter(char, info, 'consonant')}
                    onSpeak={handleSpeak}
                  />
                ))}
              </div>
            </TabsContent>

            {/* Vowels */}
            <TabsContent value="vowels" className="animate-fade-in">
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-4" data-testid="vowels-grid">
                {vowels.map((v) => (
                  <CharacterCard
                    key={v.char}
                    char={v.char}
                    info={v}
                    progress={getProgressForChar(v.char)}
                    onSelect={(char, info) => handleSelectCharacter(char, info, 'vowel')}
                    onSpeak={handleSpeak}
                  />
                ))}
              </div>
            </TabsContent>

            {/* Numbers */}
            <TabsContent value="numbers" className="animate-fade-in">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-5 mb-6 border border-blue-100/50">
                <h3 className="font-heading text-lg text-blue-800 mb-2 flex items-center gap-2">
                  <span className="font-thai">ตัวเลขไทย</span> - Les Chiffres Thaïs
                </h3>
                <p className="text-sm text-blue-700 leading-relaxed">
                  Les chiffres thaïs sont utilisés dans les documents officiels, les temples, et parfois les prix. 
                  Les chiffres arabes (0-9) sont aussi très courants en Thaïlande.
                </p>
              </div>
              <div className="grid grid-cols-5 sm:grid-cols-5 md:grid-cols-10 gap-4" data-testid="numbers-grid">
                {numbers.map((n) => (
                  <div
                    key={n.char}
                    onClick={() => handleSelectCharacter(n.char, n, 'number')}
                    className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-blue-400 text-center"
                  >
                    <div className="font-thai text-5xl text-blue-600 mb-2">{n.char}</div>
                    <div className="text-2xl text-slate-400 mb-1">{n.arabic}</div>
                    <div className="font-thai text-sm text-slate-600">{n.name}</div>
                    <div className="text-xs text-slate-500">{n.meaning}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSpeak(n.name);
                      }}
                      className="mt-2 p-1.5 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Classifiers */}
            <TabsContent value="classifiers">
              <div className="space-y-4" data-testid="classifiers-grid">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 mb-6">
                  <h3 className="font-heading text-lg text-indigo-800 mb-2">
                    ลักษณนาม - Les Classificateurs Thaïs
                  </h3>
                  <p className="text-sm text-indigo-700">
                    En thaï, quand on compte des objets, on utilise un classificateur entre le nombre et le nom.
                    Par exemple : <span className="font-thai">หมา <strong>สอง ตัว</strong></span> = deux chiens (littéralement : chien deux [classificateur animal])
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {classifiers.map((clf, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`classifier-${idx}`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-thai text-3xl text-indigo-600">{clf.thai}</span>
                            <button
                              onClick={() => handleSpeak(clf.thai)}
                              className="p-2 bg-indigo-100 rounded-full hover:bg-indigo-200 transition-colors"
                            >
                              <Volume2 className="w-4 h-4 text-indigo-600" />
                            </button>
                          </div>
                          <p className="text-sm text-slate-500">{clf.pronunciation}</p>
                        </div>
                        <Badge className="bg-indigo-100 text-indigo-700">
                          {clf.french}
                        </Badge>
                      </div>

                      {/* Usage */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-slate-700 mb-1">Usage :</p>
                        <p className="text-sm text-slate-600">{clf.usage}</p>
                      </div>

                      {/* Examples */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">Exemples :</p>
                        <div className="space-y-1">
                          {clf.examples.map((ex, exIdx) => (
                            <div key={exIdx} className="flex items-center gap-2 text-sm">
                              <button
                                onClick={() => handleSpeak(ex.thai)}
                                className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                              >
                                <Volume2 className="w-3 h-3 text-slate-400" />
                              </button>
                              <span className="font-thai text-indigo-600">{ex.thai}</span>
                              <span className="text-slate-400">→</span>
                              <span className="text-slate-600">{ex.french}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Tip */}
                      <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                        <p className="text-xs text-amber-800">
                          <span className="font-medium">💡 Astuce :</span> {clf.tip}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Words */}
            <TabsContent value="words">
              <div className="space-y-6">
                {/* Category Filter and Search */}
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher un mot..."
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-full focus:outline-none focus:border-amber-500"
                        data-testid="search-words-input"
                      />
                    </div>
                    {/* Category Select */}
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-slate-500" />
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-4 py-2 border border-slate-200 rounded-full focus:outline-none focus:border-amber-500 bg-white"
                        data-testid="category-filter-select"
                      >
                        <option value="all">Toutes catégories ({allWords.length})</option>
                        {[...new Set(allWords.map(w => w.category))].sort().map(cat => (
                          <option key={cat} value={cat}>
                            {cat} ({allWords.filter(w => w.category === cat).length})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Category Pills */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className={`px-3 py-1 rounded-full text-sm transition-all ${
                        selectedCategory === "all" 
                          ? "bg-amber-500 text-white" 
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid="category-pill-all"
                    >
                      Tous ({allWords.length})
                    </button>
                    {[...new Set(allWords.map(w => w.category))].sort().map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          selectedCategory === cat 
                            ? "bg-amber-500 text-white" 
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`category-pill-${cat}`}
                      >
                        {cat} ({allWords.filter(w => w.category === cat).length})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filtered Words (Sample + Custom combined) */}
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-amber-500" />
                    {selectedCategory === "all" ? "Tous les mots" : selectedCategory}
                    <span className="text-sm font-normal text-slate-500">
                      ({allWords.filter(w => 
                        (selectedCategory === "all" || w.category === selectedCategory) &&
                        (searchQuery === "" || 
                          w.thai.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          w.french.toLowerCase().includes(searchQuery.toLowerCase()))
                      ).length} mots)
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="words-grid">
                    {allWords
                      .filter(w => 
                        (selectedCategory === "all" || w.category === selectedCategory) &&
                        (searchQuery === "" || 
                          w.thai.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          w.french.toLowerCase().includes(searchQuery.toLowerCase()))
                      )
                      .map((w, idx) => (
                      <div
                        key={w.id || idx}
                        onClick={() => handleSelectCharacter(w.thai, { meaning: w.french, context: w.category }, 'word')}
                        className={`bg-white rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-amber-400 relative group ${w.is_custom ? 'ring-2 ring-teal-200' : ''}`}
                        data-testid={`word-${w.id || idx}`}
                      >
                        {w.is_custom && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteWord(w.id); }}
                            className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 hover:bg-red-100 rounded-full transition-all"
                            data-testid={`delete-word-${w.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                        <p className="font-thai text-2xl text-center text-slate-800">{w.thai}</p>
                        <p className="text-sm text-center text-slate-500 mt-1">{w.french}</p>
                        <Badge 
                          variant="outline" 
                          className={`mt-2 w-full justify-center text-xs ${w.is_custom ? 'bg-teal-50 border-teal-200' : ''}`}
                        >
                          {w.category} {w.is_custom && '★'}
                        </Badge>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSpeak(w.thai); }}
                          className={`w-full mt-3 flex items-center justify-center gap-1 py-2 rounded-full transition-colors text-sm ${
                            w.is_custom 
                              ? 'bg-teal-100 text-teal-700 hover:bg-teal-200' 
                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          }`}
                        >
                          <Volume2 className="w-3 h-3" />
                          Écouter
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* No results message */}
                  {allWords.filter(w => 
                    (selectedCategory === "all" || w.category === selectedCategory) &&
                    (searchQuery === "" || 
                      w.thai.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      w.french.toLowerCase().includes(searchQuery.toLowerCase()))
                  ).length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Aucun mot trouvé pour "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Practice Modal */}
      <PracticeModal
        isOpen={practiceModal.open}
        onClose={() => setPracticeModal({ open: false, char: null, info: null, type: null })}
        character={practiceModal.char}
        info={practiceModal.info}
        type={practiceModal.type}
        onSpeak={handleSpeak}
        onComplete={handlePracticeComplete}
      />

      {/* Add Word Modal */}
      <AddWordModal
        isOpen={addWordModal}
        onClose={() => setAddWordModal(false)}
        onSave={handleSaveWord}
        categories={categories}
      />

      {/* Story Generator Modal */}
      <StoryGeneratorModal
        isOpen={storyModal}
        onClose={() => setStoryModal(false)}
      />

      {/* Intensive Training Modal */}
      <IntensiveTrainingModal
        isOpen={trainingModal}
        onClose={() => setTrainingModal(false)}
      />

      {/* Classifier Exercise Modal */}
      <ClassifierExerciseModal
        isOpen={classifierExerciseModal}
        onClose={() => setClassifierExerciseModal(false)}
      />

      {/* Progression Modal */}
      <ProgressionModal
        isOpen={progressionModal}
        onClose={() => setProgressionModal(false)}
      />

      {/* SRS Modal - Conditional rendering to prevent useOfflineSRS hook from running before beta access */}
      {srsModal && (
        <SRSModal
          isOpen={srsModal}
          onClose={() => setSrsModal(false)}
        />
      )}

      {/* Export/Import Modal */}
      <ExportImportModal
        isOpen={exportImportModal}
        onClose={() => setExportImportModal(false)}
        onSuccess={() => {
          // Refresh words data after import
          fetchAllWordsData();
        }}
      />

      {/* Manee Chat Modal */}
      <ManeeChat
        isOpen={isManeeOpen}
        onClose={() => setIsManeeOpen(false)}
        context={null}
      />

      {/* Floating Manee Button - positioned to avoid Emergent badge on mobile */}
      <button
        onClick={openManeeWithTracking}
        className="fixed z-40 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center group"
        style={{ 
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 70px)',
          right: '16px'
        }}
        data-testid="ploy-chat-button"
        title="Parler à Manee"
        aria-label="Ouvrir le chat avec Manee"
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 animate-pulse opacity-50"></div>
        <span className="relative font-thai text-xl sm:text-2xl font-bold">ม</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full border-2 border-white"></span>
        <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Parler à Manee 🎤
        </div>
      </button>

      {/* Onboarding for new users */}
      {showOnboarding && (
        <Onboarding onComplete={handleOnboardingComplete} />
      )}
    </div>
  );
}

export default App;
